'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Calendar, Clock, MapPin, Users, Filter, Search, Plus, ChevronRight, Award, User, Trash2, CheckSquare, Square, Edit2, Eye, X, MessageCircle } from 'lucide-react'
import { useGlobalUnreadStore } from '@/store/chatStore'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { CardSkeleton } from '@/components/ui/Skeleton'
import { toast } from '@/components/ui/ToastContainer'
import logger from '@/utils/logger'
import { kakaoIdToUuid } from '@/lib/utils/uuid'
import { useTheme } from '@/contexts/ThemeContext'
import { parseAuthToken } from '@/utils/base64'
import dynamic from 'next/dynamic'

// MatchingSuccessModal을 동적으로 임포트
const MatchingSuccessModal = dynamic(() => import('@/components/Matching/MatchingSuccessModal'), {
  ssr: false
})

import ProfileModal from '@/components/ProfileModal'
import BuddyReviewModalV2, { ReviewSubmitData } from '@/components/BuddyReviewModalV2'

interface MatchingRequest {
  id: string
  user_id: string
  date: string
  time: string
  time_slot?: string
  time_flexible?: boolean
  level_flexible?: boolean
  location: string
  region?: string
  diving_center?: string
  depth_preference?: string
  experience_level: string
  description?: string
  created_at: string
  status?: string
  preferred_group_size?: number
  user_profiles?: {
    name: string
    certification_level: string
    profile_image: string | null
    region: string
  }
  matched_members?: Array<{
    id: string
    name: string
    certification_level: string
    profile_image: string | null
    region: string
  }> | null
  member_count?: number
  is_host?: boolean // 호스트(매칭 생성자) 여부
}

// 시간대를 한글로 변환하는 함수
const translateTimeSlot = (timeSlot: string | undefined | null): string => {
  if (!timeSlot) return '시간 미정'
  const slot = timeSlot.toLowerCase()
  switch (slot) {
    case 'morning':
    case '오전':
      return '오전'
    case 'afternoon':
    case '오후':
      return '오후'
    case 'evening':
    case '저녁':
      return '저녁'
    case 'flexible':
      return '시간상관없음'
    default:
      return timeSlot || '시간 미정'
  }
}

export default function MatchingPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const { totalUnread: unreadChatCount } = useGlobalUnreadStore()
  const [activeTab, setActiveTab] = useState<'all' | 'mine'>('all')
  const [userProfile, setUserProfile] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingRequests, setIsLoadingRequests] = useState(true)
  const [requests, setRequests] = useState<MatchingRequest[]>([])
  const [publicRequests, setPublicRequests] = useState<any[]>([])
  const [isLoadingPublic, setIsLoadingPublic] = useState(true)
  const [filter, setFilter] = useState({
    date: '',
    location: '',
    level: ''
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedMatchInfo, setSelectedMatchInfo] = useState<any>(null)
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [subscription, setSubscription] = useState<any>(null)
  const [showMemberSelect, setShowMemberSelect] = useState<string | null>(null) // 멤버 선택 드롭다운 표시할 request id
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  // expire API 중복 호출 방지용 ref
  const expireCalledRef = useRef(false)
  const [reviewMatchInfo, setReviewMatchInfo] = useState<{
    matchRequestId: string
    date: string
    location: string
    buddyNames: string[]
    buddyIds: string[]
  } | null>(null)

  useEffect(() => {
    const init = async () => {
      await checkAuth()
      loadMatchingRequests()
    }
    init()
    loadPublicRequests()
    // loadSubscriptionStatus() // API route not implemented yet
  }, [])

  // 외부 클릭 시 멤버 선택 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = () => {
      if (showMemberSelect) {
        setShowMemberSelect(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showMemberSelect])

  // const loadSubscriptionStatus = async () => {
  //   try {
  //     const token = localStorage.getItem('auth_token')
  //     if (!token) return

  //     const response = await fetch('/api/subscription/status', {
  //       headers: { Authorization: `Bearer ${token}` }
  //     })
  //     const result = await response.json()
  //     if (result.success) {
  //       setSubscription(result.data)
  //     }
  //   } catch (error) {
  //     console.error('Failed to load subscription:', error)
  //   }
  // }

  const loadPublicRequests = async () => {
    setIsLoadingPublic(true)
    try {
      const today = new Date().toISOString().slice(0, 10)
      const res = await fetch(`/api/matching/public?date=${today}`)
      const json = await res.json()
      if (json.error) {
        logger.error('Public requests API error:', json.error, json.detail)
      }
      if (Array.isArray(json.data)) {
        setPublicRequests(json.data)
      }
    } catch (e) {
      logger.error('Public requests load error:', e)
    } finally {
      setIsLoadingPublic(false)
    }
  }

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      
      if (!token) {
        router.push('/login')
        return
      }

      const decoded = parseAuthToken(token)
      if (!decoded) {
        router.push('/login')
        return
      }

      const supabase = getSupabaseClient()

      // 카카오 또는 네이버 로그인인지 확인하여 조회
      let query = supabase
        .from('user_profiles')
        .select('*')

      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        router.push('/login')
        return
      }

      const { data } = await query.single()

      if (!data) {
        // 프로필이 없으면 (탈퇴한 사용자) 토큰 제거하고 홈으로 리다이렉트
        localStorage.removeItem('auth_token')
        window.location.href = '/'
        return
      }

      if (!data.profile_completed) {
        router.push('/profile/setup')
        return
      }

      setUserProfile(data)
    } catch (error) {
      logger.error('Auth error:', error)
      router.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  const loadMatchingRequests = async () => {
    setIsLoadingRequests(true)
    try {
      // 먼저 만료된 매칭 처리 (세션당 1회만, 429 방지)
      if (!expireCalledRef.current) {
        const lastExpireCheck = sessionStorage.getItem('lastExpireCheck')
        const now = Date.now()
        // 마지막 호출 후 5분이 지났으면 다시 호출
        if (!lastExpireCheck || now - parseInt(lastExpireCheck) > 5 * 60 * 1000) {
          expireCalledRef.current = true
          sessionStorage.setItem('lastExpireCheck', now.toString())
          fetch('/api/matching/expire', { method: 'POST' })
            .then(res => res.json())
            .then(result => {
              if (result.expiredCount > 0) {
                logger.debug(`⏰ ${result.expiredCount}개 매칭이 종료되었습니다`)
              }
            })
            .catch(err => logger.error('Expire check failed:', err))
        }
      }

      const supabase = getSupabaseClient()

      // 현재 사용자 ID 가져오기
      const token = localStorage.getItem('auth_token')
      let currentUserId = null

      if (token) {
        const decoded = parseAuthToken(token)

        // user_profiles에서 실제 UUID 가져오기 - 카카오/네이버 구분
        if (decoded && (decoded.kakao_id || decoded.naver_id)) {
          let query = supabase
            .from('user_profiles')
            .select('id')

          if (decoded.kakao_id) {
            query = query.eq('kakao_id', decoded.kakao_id)
          } else if (decoded.naver_id) {
            query = query.eq('naver_id', decoded.naver_id)
          }

          const { data: userProfile } = await query.single()

          if (userProfile) {
            currentUserId = userProfile.id
          }
        }
      }

      if (!currentUserId) {
        // 토큰 없거나 인증 전 상태 - checkAuth()가 리다이렉트 처리함
        return
      }

      // Supabase에서 매칭 요청 가져오기 (사용자의 매칭만)
      const { data: requestsData, error } = await supabase
        .from('match_requests')
        .select('*')
        .eq('user_id', currentUserId)
        .in('status', ['pending', 'matched', 'confirmed', 'expired'])
        .limit(20)
        .order('created_at', { ascending: false })

      if (error) throw error

      // 각 요청에 대해 같은 조건의 참여자 수를 계산하여 정원이 찼는지 확인
      const requestsWithMemberCount = await Promise.all(
        (requestsData || []).map(async (request) => {
          // 같은 조건(날짜/지역/센터/시간/인원수)의 참여자 수 카운트
          let memberCountQuery = supabase
            .from('match_requests')
            .select('id', { count: 'exact', head: true })
            .eq('date', request.date)
            .eq('region', request.region)
            .in('status', ['pending', 'matched', 'confirmed'])

          if (request.diving_center) {
            memberCountQuery = memberCountQuery.eq('diving_center', request.diving_center)
          }
          if (request.time_slot) {
            memberCountQuery = memberCountQuery.eq('time_slot', request.time_slot)
          }
          if (request.preferred_group_size) {
            memberCountQuery = memberCountQuery.eq('preferred_group_size', request.preferred_group_size)
          }

          const { count } = await memberCountQuery

          const memberCount = count || 1
          const maxMembers = request.preferred_group_size || 4
          const isFull = memberCount >= maxMembers

          // 정원이 찼으면 status를 matched로 표시 (실제 DB는 pending일 수 있음)
          const displayStatus = isFull && request.status === 'pending' ? 'matched' : request.status

          return {
            ...request,
            status: displayStatus,
            actual_member_count: memberCount
          }
        })
      )

      // 매칭된 요청의 경우 상대방 프로필 가져오기
      const requestsWithProfiles = await Promise.all(
        requestsWithMemberCount.map(async (request) => {
          if (request.status === 'matched' || request.status === 'confirmed' || request.status === 'expired') {
            // 호스트 여부 확인: 같은 그룹에서 더 일찍 생성된 요청이 있는지 확인
            let isHost = true
            try {
              let hostCheckQuery = supabase
                .from('match_requests')
                .select('id')
                .eq('date', request.date)
                .eq('region', request.region)
                .in('status', ['pending', 'matched', 'confirmed', 'expired'])
                .lt('created_at', request.created_at)
                .limit(1)

              if (request.diving_center) {
                hostCheckQuery = hostCheckQuery.eq('diving_center', request.diving_center)
              }

              const { data: earlierRequests } = await hostCheckQuery
              if (earlierRequests && earlierRequests.length > 0) {
                isHost = false
              }
            } catch (error) {
              logger.error('Failed to check host status:', error)
            }

            // 매칭된 참여자 정보 가져오기 (같은 날짜/지역/센터로 그룹 조회)
            try {
              // 같은 그룹의 모든 요청 조회 (본인 포함) - pending도 포함 (정원이 찬 경우 displayStatus만 matched)
              let groupQuery = supabase
                .from('match_requests')
                .select('user_id')
                .eq('date', request.date)
                .eq('region', request.region)
                .in('status', ['pending', 'matched', 'confirmed', 'expired'])

              // 조건별 그룹 구분 (홈화면과 동일한 조건)
              if (request.diving_center) {
                groupQuery = groupQuery.eq('diving_center', request.diving_center)
              }
              if (request.time_slot) {
                groupQuery = groupQuery.eq('time_slot', request.time_slot)
              }
              if (request.preferred_group_size) {
                groupQuery = groupQuery.eq('preferred_group_size', request.preferred_group_size)
              }

              const { data: groupRequests } = await groupQuery

              if (groupRequests && groupRequests.length > 0) {
                // 모든 멤버 ID 수집
                const memberIds = groupRequests.map(r => r.user_id)

                // 모든 멤버의 프로필 가져오기
                const { data: memberProfiles } = await supabase
                  .from('user_profiles')
                  .select('id, name, certification_level, profile_image, region')
                  .in('id', memberIds)

                if (memberProfiles && memberProfiles.length > 0) {
                  // 첫 번째 멤버를 대표로 표시 (카드 UI용)
                  const primaryMember = memberProfiles[0]

                  return {
                    ...request,
                    user_profiles: {
                      name: memberProfiles.length === 1
                        ? primaryMember.name
                        : `${primaryMember.name} 외 ${memberProfiles.length - 1}명`,
                      certification_level: primaryMember.certification_level,
                      profile_image: primaryMember.profile_image,
                      region: primaryMember.region
                    },
                    matched_members: memberProfiles, // 전체 멤버 정보 저장 (본인 포함)
                    member_count: request.actual_member_count || memberProfiles.length, // 조건별 카운트 우선 사용
                    is_host: isHost
                  }
                }
              }
            } catch (error) {
              logger.error('Failed to load match partner info:', error)
            }

            // 매칭 정보를 가져오지 못한 경우 기본값
            return {
              ...request,
              user_profiles: {
                name: '매칭된 다이버',
                certification_level: 'student',
                profile_image: null,
                region: '지역 정보 없음'
              },
              matched_members: null,
              member_count: request.actual_member_count || 1,
              is_host: isHost
            }
          }

          // pending 상태는 본인 프로필 표시 + 호스트 여부 확인
          const token = localStorage.getItem('auth_token')

          // 호스트 여부 확인: 같은 그룹에서 더 일찍 생성된 요청이 있는지 확인
          let isHost = true
          try {
            let hostCheckQuery = supabase
              .from('match_requests')
              .select('id, created_at')
              .eq('date', request.date)
              .eq('region', request.region)
              .in('status', ['pending', 'matched', 'confirmed'])
              .lt('created_at', request.created_at) // 더 일찍 생성된 요청이 있는지
              .limit(1)

            if (request.diving_center) {
              hostCheckQuery = hostCheckQuery.eq('diving_center', request.diving_center)
            }

            const { data: earlierRequests } = await hostCheckQuery

            // 더 일찍 생성된 요청이 있으면 참여자 (호스트 아님)
            if (earlierRequests && earlierRequests.length > 0) {
              isHost = false
            }
          } catch (error) {
            logger.error('Failed to check host status:', error)
          }

          if (token) {
            const decoded = parseAuthToken(token)

            // 카카오 또는 네이버 로그인인지 확인하여 조회
            if (decoded && (decoded.kakao_id || decoded.naver_id)) {
              let query = supabase
                .from('user_profiles')
                .select('name, certification_level, profile_image, region')

              if (decoded.kakao_id) {
                query = query.eq('kakao_id', decoded.kakao_id)
              } else if (decoded.naver_id) {
                query = query.eq('naver_id', decoded.naver_id)
              }

              const { data: profile } = await query.single()

              return {
                ...request,
                user_profiles: profile,
                member_count: request.actual_member_count || 1,
                is_host: isHost
              }
            }
          }

          return {
            ...request,
            user_profiles: null,
            member_count: request.actual_member_count || 1,
            is_host: isHost
          }
        })
      )
      
      const data = requestsWithProfiles
      
      setRequests(data || [])
      // if (data && data.length > 0) {
      //   toast.success('매칭 요청을 불러왔습니다', `${data.length}개의 매칭 요청이 있습니다.`)
      // }
    } catch (error) {
      logger.error('Error loading matching requests:', error)
      toast.error('네트워크 오류', '연결을 확인한 후 다시 시도해주세요.')
      setRequests([])
    } finally {
      setIsLoadingRequests(false)
    }
  }

  const getLevelDisplay = (level: string) => {
    const levels: Record<string, string> = {
      // 초급자
      'student': '교육생',
      'beginner': '입문자',
      
      // AIDA 프리다이빙
      'aida1': 'AIDA 1',
      'aida2': 'AIDA 2', 
      'aida3': 'AIDA 3',
      'aida4': 'AIDA 4',
      
      // CMAS 프리다이빙
      'cmas1': 'CMAS 1',
      'cmas2': 'CMAS 2',
      'cmas3': 'CMAS 3', 
      'cmas4': 'CMAS 4',
      
      // SSI 프리다이빙
      'ssi_basic': 'SSI Basic',
      'ssi_level1': 'SSI Lv1',
      'ssi_level2': 'SSI Lv2',
      'ssi_level3': 'SSI Lv3',
      
      // PADI 프리다이빙
      'padi_basic': 'PADI Basic',
      'padi_freediver': 'PADI Freediver',
      'padi_advanced': 'PADI Adv',
      'padi_master': 'PADI Master',
      
      // RAID 프리다이빙
      'raid_basic': 'RAID Basic',
      'raid_freediver': 'RAID Freediver',
      'raid_advanced': 'RAID Adv',
      'raid_master': 'RAID Master',
      
      // 스쿠버다이빙 (기존 호환성)
      'open_water': 'Open Water',
      'advanced': 'Advanced',
      'rescue': 'Rescue',
      'divemaster': 'Divemaster',
      
      // 강사급
      'instructor': '강사',
      'instructor_trainer': '강사 트레이너',
      
      // 기존 호환성 (구버전)
      'padi_ow': 'PADI OW',
      'padi_aow': 'PADI AOW',
      'padi_rescue': 'PADI Rescue',
      'padi_dm': 'PADI DM',
      'ssi_ow': 'SSI OW',
      'ssi_aow': 'SSI AOW',
      'ssi_dm': 'SSI DM'
    }
    return levels[level] || level
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === requests.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(requests.map(r => r.id))
    }
  }

  const toggleSelect = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  // 매칭 상세 정보 불러오기
  const loadMatchDetails = async (requestId: string) => {
    try {
      const supabase = getSupabaseClient()

      // 현재 사용자 ID 가져오기
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const decoded = parseAuthToken(token)
      if (!decoded) return

      // 카카오 또는 네이버 로그인인지 확인하여 조회
      let query = supabase
        .from('user_profiles')
        .select('id')

      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        return
      }

      const { data: myProfile } = await query.single()

      if (!myProfile) return

      // 매칭 요청 정보 조회
      const { data: myRequest } = await supabase
        .from('match_requests')
        .select('*')
        .eq('id', requestId)
        .single()

      if (!myRequest) {
        toast.error('매칭 정보를 찾을 수 없습니다')
        return
      }

      // 같은 그룹의 다른 매칭 요청 조회 (같은 조건 + pending도 포함)
      let groupQuery = supabase
        .from('match_requests')
        .select('user_id')
        .eq('date', myRequest.date)
        .eq('region', myRequest.region)
        .in('status', ['pending', 'matched', 'confirmed'])
        .neq('user_id', myProfile.id)

      // 조건별 그룹 구분 (홈화면과 동일한 조건)
      if (myRequest.diving_center) {
        groupQuery = groupQuery.eq('diving_center', myRequest.diving_center)
      }
      if (myRequest.time_slot) {
        groupQuery = groupQuery.eq('time_slot', myRequest.time_slot)
      }
      if (myRequest.preferred_group_size) {
        groupQuery = groupQuery.eq('preferred_group_size', myRequest.preferred_group_size)
      }

      const { data: groupRequests } = await groupQuery

      if (!groupRequests || groupRequests.length === 0) {
        toast.error('매칭된 다이버 정보를 찾을 수 없습니다')
        return
      }

      // 첫 번째 매칭 상대 프로필 조회
      const { data: partnerProfile } = await supabase
        .from('user_profiles')
        .select('name, certification_level')
        .eq('id', groupRequests[0].user_id)
        .single()

      if (!partnerProfile) {
        toast.error('상대방 정보를 찾을 수 없습니다')
        return
      }

      // 모든 참여자 ID 수집 (본인 포함)
      const allParticipantIds = [myProfile.id, ...groupRequests.map(r => r.user_id)]

      // group_match_id 사용 (matches 테이블의 ID, 채팅방 조회용)
      // 없으면 requestId를 fallback으로 사용
      const groupMatchId = myRequest.group_match_id || requestId

      logger.debug('📝 loadMatchDetails - 참여자 수:', allParticipantIds.length, '그룹 매칭:', allParticipantIds.length > 2, 'group_match_id:', groupMatchId)

      // 모달에 표시할 정보 설정
      setSelectedMatchInfo({
        matchId: groupMatchId, // matches 테이블의 ID 사용 (채팅방 조회용)
        partnerName: groupRequests.length === 1
          ? partnerProfile.name
          : `${partnerProfile.name} 외 ${groupRequests.length - 1}명`,
        partnerLevel: partnerProfile.certification_level,
        partnerUserId: groupRequests[0].user_id,
        participantIds: allParticipantIds, // 모든 참여자 ID 전달 (그룹 채팅방 생성용)
        date: myRequest.date,
        time: myRequest.time_slot,
        location: myRequest.diving_center || myRequest.location,
        region: myRequest.region
      })
      setShowMatchModal(true)

    } catch (error) {
      logger.error('매칭 정보 조회 오류:', error)
      toast.error('매칭 정보를 불러올 수 없습니다')
    }
  }

  // 단일 매칭 취소 핸들러
  const handleCancelMatch = async (requestId: string) => {
    if (!confirm('이 매칭을 취소하시겠습니까?')) {
      return
    }

    // 🚀 Optimistic UI: 서버 응답 전에 UI 먼저 업데이트
    const previousRequests = [...requests]
    setRequests(prev => prev.filter(r => r.id !== requestId))
    toast.success('취소 완료', '매칭이 취소되었습니다.')

    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('로그인이 필요합니다')
      }

      const response = await fetch('/api/matching/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          request_ids: [requestId]
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '취소에 실패했습니다')
      }

      // 백그라운드에서 최신 데이터 동기화
      loadMatchingRequests()
    } catch (error: any) {
      // 🔄 실패 시 롤백
      logger.error('Cancel error:', error)
      setRequests(previousRequests)
      toast.error('취소 실패', error.message || '잠시 후 다시 시도해주세요.')
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error('선택 오류', '삭제할 항목을 선택해주세요.')
      return
    }

    if (!confirm(`선택한 ${selectedIds.length}개의 매칭 내역을 삭제하시겠습니까?`)) {
      return
    }

    // 🚀 Optimistic UI: 서버 응답 전에 UI 먼저 업데이트
    const previousRequests = [...requests]
    const deletedCount = selectedIds.length
    setRequests(prev => prev.filter(r => !selectedIds.includes(r.id)))
    setSelectedIds([])
    toast.success('삭제 완료', `${deletedCount}개의 매칭 내역이 삭제되었습니다.`)

    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('로그인이 필요합니다')
      }

      const response = await fetch('/api/matching/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          request_ids: selectedIds
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '삭제에 실패했습니다')
      }

      // 백그라운드에서 최신 데이터 동기화
      loadMatchingRequests()
    } catch (error: any) {
      // 🔄 실패 시 롤백
      logger.error('Delete error:', error)
      setRequests(previousRequests)
      toast.error('삭제 실패', error.message || '잠시 후 다시 시도해주세요.')
    }
  }

  // 평가 모달 열기
  const openReviewModal = (request: MatchingRequest) => {
    if (!request.matched_members || request.matched_members.length === 0) {
      toast.error('평가 오류', '평가할 버디 정보가 없습니다.')
      return
    }

    setReviewMatchInfo({
      matchRequestId: request.id,
      date: request.date,
      location: request.diving_center || request.region || '다이빙 장소',
      buddyNames: request.matched_members.map(m => m.name),
      buddyIds: request.matched_members.map(m => m.id)
    })
    setIsReviewModalOpen(true)
  }

  // 평가 제출
  const handleReviewSubmit = async (data: ReviewSubmitData) => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      toast.error('로그인 필요', '로그인이 필요합니다.')
      return
    }

    const response = await fetch('/api/buddy-review/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.error || '평가 제출에 실패했습니다')
    }

    toast.success('평가 완료', '버디 평가가 성공적으로 제출되었습니다!')
    loadMatchingRequests() // 목록 새로고침
  }

  const getLevelColor = (level: string) => {
    const colors: Record<string, string> = {
      // 초급자
      'student': 'bg-gray-500/20 text-gray-400',
      'beginner': 'bg-slate-500/20 text-slate-400',
      
      // AIDA 프리다이빙 (초록 계열)
      'aida1': 'bg-green-500/20 text-green-400',
      'aida2': 'bg-emerald-500/20 text-emerald-400',
      'aida3': 'bg-teal-500/20 text-teal-400',
      'aida4': 'bg-blue-500/20 text-blue-400',
      
      // CMAS 프리다이빙 (파랑 계열)
      'cmas1': 'bg-sky-500/20 text-sky-400',
      'cmas2': 'bg-blue-500/20 text-blue-400',
      'cmas3': 'bg-indigo-500/20 text-indigo-400',
      'cmas4': 'bg-violet-500/20 text-violet-400',
      
      // SSI 프리다이빙 (주황 계열)
      'ssi_basic': 'bg-amber-500/20 text-amber-400',
      'ssi_level1': 'bg-orange-500/20 text-orange-400',
      'ssi_level2': 'bg-red-500/20 text-red-400',
      'ssi_level3': 'bg-rose-500/20 text-rose-400',
      
      // PADI 프리다이빙 (보라 계열)
      'padi_basic': 'bg-purple-500/20 text-purple-400',
      'padi_freediver': 'bg-fuchsia-500/20 text-fuchsia-400',
      'padi_advanced': 'bg-pink-500/20 text-pink-400',
      'padi_master': 'bg-rose-600/20 text-rose-400',
      
      // RAID 프리다이빙 (황금 계열)
      'raid_basic': 'bg-yellow-500/20 text-yellow-400',
      'raid_freediver': 'bg-lime-500/20 text-lime-400',
      'raid_advanced': 'bg-green-600/20 text-green-400',
      'raid_master': 'bg-emerald-600/20 text-emerald-400',
      
      // 스쿠버다이빙 (기존 호환성)
      'open_water': 'bg-green-500/20 text-green-400',
      'advanced': 'bg-blue-500/20 text-blue-400',
      'rescue': 'bg-purple-500/20 text-purple-400',
      'divemaster': 'bg-orange-500/20 text-orange-400',
      
      // 강사급 (빨강 계열)
      'instructor': 'bg-red-500/20 text-red-400',
      'instructor_trainer': 'bg-red-600/20 text-red-300',
      
      // 기존 호환성 (구버전)
      'padi_ow': 'bg-blue-500/20 text-blue-400',
      'padi_aow': 'bg-indigo-500/20 text-indigo-400',
      'padi_rescue': 'bg-violet-500/20 text-violet-400',
      'padi_dm': 'bg-purple-500/20 text-purple-400',
      'ssi_ow': 'bg-amber-500/20 text-amber-400',
      'ssi_aow': 'bg-orange-500/20 text-orange-400',
      'ssi_dm': 'bg-red-500/20 text-red-400'
    }
    return colors[level] || 'bg-gray-500/20 text-gray-400'
  }

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'
      }`}>
        <div className={theme === 'light' ? 'text-gray-900' : 'text-white'}>로딩 중...</div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen ${
      theme === 'light' ? 'bg-[#f4f7fb]' : 'bg-[#0f172a]'
    }`}>
      {/* 히어로 헤더 */}
      <div className={`relative overflow-hidden ${
        theme === 'light' ? 'bg-[#0a1f3a]' : 'bg-[#1e2a3d]'
      }`}>
        <div className="max-w-7xl mx-auto px-2 xxs:px-3 xs:px-4 py-3 xxs:py-4 xs:py-6">
          <div className="flex justify-between items-center gap-2">
            {/* rising bubbles decoration */}
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
              <span className="od-rise-dot" />
              <span className="od-rise-dot" />
              <span className="od-rise-dot" />
              <span className="od-rise-dot" />
            </div>
            <div className="relative min-w-0">
              <p style={{ font: '400 11px/16px sans-serif', color: 'rgba(255,255,255,0.55)', margin: '0 0 4px' }}>
                버디 매칭
              </p>
              <h1 style={{ font: '700 20px/28px sans-serif', color: '#fff', margin: '0 0 2px' }}>
                {activeTab === 'all' ? '버디 찾기' : '내 매칭'}
              </h1>
              <p style={{ font: '400 13px/20px sans-serif', color: 'rgba(255,255,255,0.65)', margin: 0 }}>
                같은 날, 같은 바다에서 다이빙할 버디
              </p>
            </div>
            <div className="flex items-center gap-1 xxs:gap-1.5 xs:gap-2 flex-shrink-0">
              {selectedIds.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                  className="px-2 xxs:px-3 xs:px-4 py-1.5 xxs:py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1 xxs:gap-1.5 xs:gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs xxs:text-sm whitespace-nowrap"
                >
                  <Trash2 className="w-3.5 xxs:w-4 xs:w-5 h-3.5 xxs:h-4 xs:h-5" />
                  <span className="hidden xxs:inline">선택 삭제</span> ({selectedIds.length})
                </button>
              )}
              <button
                onClick={() => router.push('/chat')}
                className="relative px-2 xxs:px-3 py-1.5 xxs:py-2 rounded-lg transition-colors flex items-center gap-1 text-xs xxs:text-sm whitespace-nowrap"
                style={{ background: 'rgba(255,255,255,0.12)', color: '#fff' }}
              >
                <MessageCircle className="w-3.5 xxs:w-4 h-3.5 xxs:h-4" />
                <span className="hidden xs:inline">채팅</span>
                {unreadChatCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadChatCount > 9 ? '9+' : unreadChatCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => router.push('/matching/new')}
                className="px-2 xxs:px-3 xs:px-4 py-1.5 xxs:py-2 rounded-lg transition-colors flex items-center gap-1 xxs:gap-1.5 xs:gap-2 text-xs xxs:text-sm whitespace-nowrap font-bold"
                style={{ background: '#2b6df0', color: '#fff' }}
              >
                <Plus className="w-3.5 xxs:w-4 xs:w-5 h-3.5 xxs:h-4 xs:h-5" />
                <span className="hidden xs:inline">매칭</span> 시작
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 탭 */}
      <div className={`flex border-b ${
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#141e2d] border-[#2c3a52]'
      }`}>
        {(['all', 'mine'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === tab
                ? theme === 'light'
                  ? 'text-[#2b6df0] border-b-2 border-[#2b6df0]'
                  : 'text-blue-400 border-b-2 border-blue-400'
                : theme === 'light'
                  ? 'text-gray-500 hover:text-gray-700'
                  : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {tab === 'all' ? '전체 목록' : '내 매칭'}
          </button>
        ))}
      </div>

      {/* 필터 섹션 */}
      <div className={`border-b ${
        theme === 'light'
          ? 'bg-[#edf1f7] border-[#d6e4f7]'
          : 'bg-[#1e2a3d] border-[#2c3a52]'
      }`}>
        <div className="max-w-7xl mx-auto px-2 xxs:px-3 xs:px-4 py-2 xxs:py-3 xs:py-4">
          <div className="flex flex-wrap gap-1.5 xxs:gap-2 xs:gap-3">
            {/* 전체 선택 체크박스 */}
            {requests.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className={`px-2 xxs:px-3 xs:px-4 py-1.5 xxs:py-2 border rounded-lg transition-colors flex items-center gap-1 xxs:gap-1.5 xs:gap-2 text-xs xxs:text-sm whitespace-nowrap ${
                  theme === 'light'
                    ? 'bg-white border-gray-300 hover:bg-gray-50'
                    : 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                }`}
              >
                {selectedIds.length === requests.length ? (
                  <CheckSquare className="w-3.5 xxs:w-4 xs:w-5 h-3.5 xxs:h-4 xs:h-5 text-blue-600" />
                ) : (
                  <Square className="w-3.5 xxs:w-4 xs:w-5 h-3.5 xxs:h-4 xs:h-5 text-gray-400" />
                )}
                <span className={`hidden xs:inline ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                  {selectedIds.length === requests.length ? '전체 해제' : '전체 선택'}
                </span>
              </button>
            )}
            {/* 검색 */}
            <div className="flex-1 min-w-[120px]">
              <div className="relative">
                <Search className="absolute left-2 xxs:left-2.5 xs:left-3 top-1/2 -translate-y-1/2 w-3.5 xxs:w-4 xs:w-5 h-3.5 xxs:h-4 xs:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="지역, 레벨 검색"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-7 xxs:pl-8 xs:pl-10 pr-2 xxs:pr-2.5 xs:pr-3 py-1.5 xxs:py-2 border rounded-lg placeholder-gray-400 focus:ring-2 focus:ring-blue-600 text-xs xxs:text-sm ${
                    theme === 'light'
                      ? 'bg-white border-gray-300 text-gray-900'
                      : 'bg-gray-700 border-gray-600 text-white'
                  }`}
                />
              </div>
            </div>

            {/* 날짜 필터 */}
            <input
              type="date"
              value={filter.date}
              onChange={(e) => setFilter({ ...filter, date: e.target.value })}
              className={`px-2 xxs:px-2.5 xs:px-3 py-1.5 xxs:py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 text-xs xxs:text-sm ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-700 border-gray-600 text-white'
              }`}
            />

            {/* 지역 필터 */}
            <select
              value={filter.location}
              onChange={(e) => setFilter({ ...filter, location: e.target.value })}
              className={`px-2 xxs:px-2.5 xs:px-3 py-1.5 xxs:py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 text-xs xxs:text-sm ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-700 border-gray-600 text-white'
              }`}
            >
              <option value="">모든 지역</option>
              <option value="서울">서울</option>
              <option value="경기">경기</option>
              <option value="인천">인천</option>
              <option value="강원">강원</option>
              <option value="제주">제주</option>
              <option value="부산">부산</option>
            </select>

            {/* 레벨 필터 */}
            <select
              value={filter.level}
              onChange={(e) => setFilter({ ...filter, level: e.target.value })}
              className={`px-2 xxs:px-2.5 xs:px-3 py-1.5 xxs:py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 text-xs xxs:text-sm ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-700 border-gray-600 text-white'
              }`}
            >
              <option value="">모든 레벨</option>
              <option value="student">교육생</option>
              <option value="open_water">Open Water</option>
              <option value="advanced">Advanced</option>
              <option value="rescue">Rescue</option>
              <option value="divemaster">Divemaster</option>
              <option value="instructor">Instructor</option>
            </select>

            {/* 필터 초기화 */}
            <button
              onClick={() => {
                setFilter({ date: '', location: '', level: '' })
                setSearchQuery('')
              }}
              className={`px-2 xxs:px-3 xs:px-4 py-1.5 xxs:py-2 rounded-lg transition-colors text-xs xxs:text-sm whitespace-nowrap ${
                theme === 'light'
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 전체 목록 탭 */}
      {activeTab === 'all' && (
        <div className="max-w-7xl mx-auto px-2 xxs:px-3 xs:px-4 py-3 xxs:py-4 xs:py-6">
          {isLoadingPublic ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`rounded-xl h-20 animate-pulse ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-800'}`} />
              ))}
            </div>
          ) : publicRequests.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-14 h-14 mx-auto mb-3 text-gray-400" />
              <p className={`text-base font-medium mb-1 ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                지금 버디를 찾는 다이버가 없습니다
              </p>
              <p className={`text-sm mb-5 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                가장 먼저 버디를 구해보세요!
              </p>
              <button
                onClick={() => router.push('/matching/new')}
                className="px-5 py-2.5 rounded-xl font-bold text-sm text-white"
                style={{ background: '#2b6df0' }}
              >
                버디 구하기
              </button>
            </div>
          ) : (
            <div className="space-y-2 xxs:space-y-3">
              {publicRequests.map(req => {
                const daysUntil = Math.ceil(
                  (new Date(req.date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                )
                const maskedName = (() => {
                  const name: string = req.user_profiles?.name || '다이버'
                  if (name.length <= 1) return name
                  if (name.length === 2) return name[0] + '*'
                  return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1]
                })()
                return (
                  <div
                    key={req.id}
                    className={`rounded-xl border p-3 xxs:p-4 transition-all cursor-pointer ${
                      theme === 'light'
                        ? 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md'
                        : 'bg-gray-800/80 border-gray-700/50 hover:border-blue-500/50'
                    }`}
                    onClick={() => router.push('/matching/new')}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-sm ${
                          theme === 'light' ? 'bg-blue-50 text-blue-700' : 'bg-blue-900/30 text-blue-400'
                        }`}>
                          {req.user_profiles?.name?.charAt(0) || '?'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <span className={`text-sm font-semibold ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                              {maskedName}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getLevelColor(req.user_profiles?.certification_level || '')}`}>
                              {getLevelDisplay(req.user_profiles?.certification_level || '')}
                            </span>
                          </div>
                          <div className={`flex flex-wrap gap-2 text-xs ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                            <span className="flex items-center gap-0.5">
                              <Calendar className="w-3 h-3" />
                              {new Date(req.date).toLocaleDateString('ko-KR')}
                              {daysUntil === 0 && <span className="ml-1 text-orange-500 font-bold">오늘!</span>}
                              {daysUntil === 1 && <span className="ml-1 text-orange-400 font-bold">내일</span>}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <Clock className="w-3 h-3" />
                              {req.time_flexible ? '시간 무관' : translateTimeSlot(req.time_slot)}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <MapPin className="w-3 h-3" />
                              {req.diving_center || req.region}
                            </span>
                            {req.preferred_group_size && (
                              <span className="flex items-center gap-0.5">
                                <Users className="w-3 h-3" />
                                {req.preferred_group_size}명
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); router.push('/matching/new') }}
                        className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                        style={{ background: '#2b6df0' }}
                      >
                        함께하기
                      </button>
                    </div>
                  </div>
                )
              })}
              <p className={`text-center text-xs pt-2 ${theme === 'light' ? 'text-gray-400' : 'text-gray-500'}`}>
                {publicRequests.length}명이 버디를 찾고 있어요
              </p>
            </div>
          )}
        </div>
      )}

      {/* 내 매칭 탭 */}
      {activeTab === 'mine' && (
      <div className="max-w-7xl mx-auto px-2 xxs:px-3 xs:px-4 py-3 xxs:py-4 xs:py-8">
        <div className="grid gap-2 xxs:gap-3 xs:gap-4">
          {isLoadingRequests ? (
            // Loading skeleton
            <div className="space-y-2 xxs:space-y-3 xs:space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <CardSkeleton key={i} className="bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700" />
              ))}
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 xxs:py-10 xs:py-12">
              <Users className="w-12 xxs:w-14 xs:w-16 h-12 xxs:h-14 xs:h-16 text-gray-400 dark:text-gray-600 mx-auto mb-3 xxs:mb-4" />
              <h3 className="text-base xxs:text-lg xs:text-xl font-medium text-gray-700 dark:text-gray-400 mb-1.5 xxs:mb-2 break-keep">매칭 요청이 없습니다</h3>
              <p className="text-xs xxs:text-sm xs:text-base text-gray-500 dark:text-gray-500 mb-4 xxs:mb-5 xs:mb-6 break-keep">첫 번째 매칭 요청을 만들어보세요!</p>
              <button
                onClick={() => router.push('/matching/new')}
                className="px-4 xxs:px-5 xs:px-6 py-2 xxs:py-2.5 xs:py-3 text-white rounded-xl transition-colors inline-flex items-center gap-1.5 xxs:gap-2 text-xs xxs:text-sm xs:text-base whitespace-nowrap font-bold"
                style={{ background: '#2b6df0' }}
              >
                <Plus className="w-4 xxs:w-4.5 xs:w-5 h-4 xxs:h-4.5 xs:h-5" />
                매칭 시작
              </button>
            </div>
          ) : (
            requests.map((request) => {
              const daysUntilMatch = Math.ceil(
                (new Date(request.date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
              )
              const isUrgent = daysUntilMatch >= 0 && daysUntilMatch <= 3

              return (
                <div
                  key={request.id}
                  onClick={() => {
                    // 모든 상태에서 상세 페이지로 이동
                    router.push(`/matching/${request.id}`)
                  }}
                  className={`backdrop-blur-xl border rounded-xl xxs:rounded-2xl transition-all duration-300 group relative overflow-hidden cursor-pointer ${
                    theme === 'light'
                      ? 'bg-white hover:shadow-xl'
                      : 'bg-gray-800/80 hover:shadow-2xl hover:shadow-blue-500/10'
                  } ${
                    selectedIds.includes(request.id)
                      ? theme === 'light'
                        ? 'border-blue-500 bg-blue-50/50 shadow-lg shadow-blue-500/20'
                        : 'border-blue-500 bg-blue-900/10 shadow-lg shadow-blue-500/20'
                      : theme === 'light'
                        ? 'border-gray-200 hover:border-blue-500/50 shadow-md'
                        : 'border-gray-700/50 hover:border-blue-500/50'
                  } ${isUrgent && request.status === 'pending' ? 'ring-2 ring-orange-500/50' : ''}`}
                >
                  {isUrgent && request.status === 'pending' && (
                    <div className="absolute top-0 right-0 bg-orange-500 text-white px-3 xxs:px-4 py-1 xxs:py-1.5 rounded-bl-xl text-xs xxs:text-sm font-bold flex items-center gap-1 shadow-lg">
                      🔥 {daysUntilMatch === 0 ? '오늘!' : `D-${daysUntilMatch}`}
                    </div>
                  )}
                  {/* 취소 버튼 - 모든 상태에서 표시 (urgent 배지와 겹치지 않게) */}
                  {!(isUrgent && request.status === 'pending') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCancelMatch(request.id)
                      }}
                      className={`absolute top-2 right-2 px-2 py-1 rounded-md text-xs font-medium transition-all z-10 ${
                        theme === 'light'
                          ? 'bg-gray-200 hover:bg-red-100 text-gray-600 hover:text-red-500'
                          : 'bg-gray-700 hover:bg-red-900/50 text-gray-400 hover:text-red-400'
                      }`}
                    >
                      취소
                    </button>
                  )}
                  {/* urgent 상태일 때는 배지 아래에 취소 버튼 */}
                  {isUrgent && request.status === 'pending' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCancelMatch(request.id)
                      }}
                      className={`absolute top-10 right-2 px-2 py-1 rounded-md text-xs font-medium transition-all z-10 ${
                        theme === 'light'
                          ? 'bg-gray-200 hover:bg-red-100 text-gray-600 hover:text-red-500'
                          : 'bg-gray-700 hover:bg-red-900/50 text-gray-400 hover:text-red-400'
                      }`}
                    >
                      취소
                    </button>
                  )}
                  <div className="p-2 xxs:p-3 xs:p-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <div className="flex gap-1.5 xxs:gap-2 xs:gap-3 sm:gap-4 flex-1 min-w-0">
                    {/* 체크박스 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleSelect(request.id)
                      }}
                      className="flex-shrink-0 w-4 xxs:w-5 xs:w-6 h-4 xxs:h-5 xs:h-6 mt-0.5 xxs:mt-1"
                    >
                      {selectedIds.includes(request.id) ? (
                        <CheckSquare className="w-full h-full text-blue-600" />
                      ) : (
                        <Square className="w-full h-full text-gray-400 hover:text-blue-600 transition-colors" />
                      )}
                    </button>

                    {/* 수정 버튼 - pending 상태이고 호스트일 때만 표시 */}
                    {request.status === 'pending' && request.is_host !== false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          // 수정 폼으로 이동하면서 데이터 전달
                          const requestData = {
                            id: request.id,
                            date: request.date,
                            time_slot: request.time_slot,
                            time_flexible: request.time_flexible,
                            level_flexible: request.level_flexible,
                            location: request.location,
                            region: request.region,
                            diving_center: request.diving_center,
                            experience_level: request.experience_level,
                            description: request.description,
                            preferred_group_size: request.preferred_group_size || 4
                          }
                          localStorage.setItem('edit_request_data', JSON.stringify(requestData))
                          router.push('/matching/new')
                        }}
                        className={`flex-shrink-0 p-1 xxs:p-1.5 xs:p-2 rounded-lg transition-colors group/edit ${
                          theme === 'light' ? 'hover:bg-blue-50' : 'hover:bg-blue-900/20'
                        }`}
                        title="수정"
                      >
                        <Edit2 className="w-3.5 xxs:w-4 xs:w-5 h-3.5 xxs:h-4 xs:h-5 text-gray-400 group-hover/edit:text-blue-600 transition-colors" />
                      </button>
                    )}
                    {/* 프로필 이미지 */}
                    <div className={`w-10 xxs:w-12 xs:w-14 sm:w-16 h-10 xxs:h-12 xs:h-14 sm:h-16 rounded-full overflow-hidden flex-shrink-0 ${
                      theme === 'light' ? 'bg-gray-200' : 'bg-gray-700'
                    }`}>
                      {request.user_profiles?.profile_image ? (
                        <img
                          src={request.user_profiles.profile_image}
                          alt={request.user_profiles.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className={`w-5 xxs:w-6 xs:w-7 sm:w-8 h-5 xxs:h-6 xs:h-7 sm:h-8 ${
                            theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                          }`} />
                        </div>
                      )}
                    </div>

                    {/* 내용 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 xxs:gap-1.5 xs:gap-2 sm:gap-3 mb-1 xxs:mb-1.5 xs:mb-2 flex-wrap">
                        <h3 className={`text-xs xxs:text-sm xs:text-base sm:text-lg font-semibold truncate ${
                          theme === 'light' ? 'text-gray-900' : 'text-white'
                        }`}>
                          {request.user_profiles?.name || '익명'}
                        </h3>
                        <span className={`px-1.5 xxs:px-2 py-0.5 xxs:py-1 rounded-full text-[9px] xxs:text-[10px] xs:text-xs font-medium flex-shrink-0 ${getLevelColor(request.user_profiles?.certification_level || '')}`}>
                          {getLevelDisplay(request.user_profiles?.certification_level || '')}
                        </span>
                        {request.status === 'confirmed' && (
                          <span className="px-1.5 xxs:px-2 py-0.5 xxs:py-1 bg-blue-500/20 border border-blue-500/30 text-blue-400 text-[9px] xxs:text-[10px] xs:text-xs rounded-full flex-shrink-0 whitespace-nowrap">
                            확정완료
                          </span>
                        )}
                        {request.status === 'matched' && (
                          <span className="px-1.5 xxs:px-2 py-0.5 xxs:py-1 bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] xxs:text-[10px] xs:text-xs rounded-full flex-shrink-0 whitespace-nowrap">
                            매칭됨
                          </span>
                        )}
                        {request.status === 'pending' && (
                          <span className="px-1.5 xxs:px-2 py-0.5 xxs:py-1 bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 text-[9px] xxs:text-[10px] xs:text-xs rounded-full flex-shrink-0 whitespace-nowrap">
                            대기중
                          </span>
                        )}
                        {request.status === 'expired' && (
                          <span className="px-1.5 xxs:px-2 py-0.5 xxs:py-1 bg-gray-500/20 border border-gray-500/30 text-gray-400 text-[9px] xxs:text-[10px] xs:text-xs rounded-full flex-shrink-0 whitespace-nowrap">
                            매칭종료
                          </span>
                        )}
                      </div>

                      <div className={`flex flex-wrap gap-2 xxs:gap-2.5 xs:gap-3 sm:gap-4 text-[10px] xxs:text-xs xs:text-sm ${
                        theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                        <div className="flex items-center gap-0.5 xxs:gap-1 whitespace-nowrap">
                          <Calendar className="w-2.5 xxs:w-3 xs:w-3.5 sm:w-4 h-2.5 xxs:h-3 xs:h-3.5 sm:h-4 flex-shrink-0" />
                          <span className="break-keep">{new Date(request.date).toLocaleDateString('ko-KR')}</span>
                        </div>
                        <div className="flex items-center gap-0.5 xxs:gap-1">
                          <Clock className="w-2.5 xxs:w-3 xs:w-3.5 sm:w-4 h-2.5 xxs:h-3 xs:h-3.5 sm:h-4 flex-shrink-0" />
                          {request.time_flexible ? (
                            <span className="px-1.5 xxs:px-2 py-0.5 xxs:py-1 bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] xxs:text-[10px] xs:text-xs rounded-full whitespace-nowrap">
                              시간 상관 없음
                            </span>
                          ) : (
                            <span className="whitespace-nowrap">{translateTimeSlot(request.time_slot) || request.time || '시간 미정'}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 xxs:gap-1 min-w-0">
                          <MapPin className="w-2.5 xxs:w-3 xs:w-3.5 sm:w-4 h-2.5 xxs:h-3 xs:h-3.5 sm:h-4 flex-shrink-0" />
                          <span className="truncate">{request.diving_center || request.location}</span>
                        </div>
                        {request.depth_preference && (
                          <div className="flex items-center gap-0.5 xxs:gap-1 whitespace-nowrap">
                            <Award className="w-2.5 xxs:w-3 xs:w-3.5 sm:w-4 h-2.5 xxs:h-3 xs:h-3.5 sm:h-4 flex-shrink-0" />
                            <span>{request.depth_preference}</span>
                          </div>
                        )}
                      </div>

                      {/* 매칭됨 또는 확정 완료 시 채팅 안내 메시지 */}
                      {(request.status === 'matched' || request.status === 'confirmed') && (
                        <div className="mt-2 xxs:mt-2.5 xs:mt-3 sm:mt-4 space-y-1.5 xxs:space-y-2">
                          <div className={`p-2 xxs:p-2.5 xs:p-3 border rounded-lg ${
                            theme === 'light'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-green-900/20 border-green-700/30'
                          }`}>
                            <p className={`text-[10px] xxs:text-xs xs:text-sm flex items-center gap-1 xxs:gap-1.5 xs:gap-2 break-keep ${
                              theme === 'light' ? 'text-green-700' : 'text-green-400'
                            }`}>
                              <Users className="w-3 xxs:w-3.5 xs:w-4 h-3 xxs:h-3.5 xs:h-4 flex-shrink-0" />
                              매칭이 완료되었습니다. 상단 채팅 메뉴에서 대화를 시작하세요.
                            </p>
                          </div>

                          {/* 매칭된 다이버 프로필 */}
                          {request.matched_members && request.matched_members.length > 0 && (
                            <div className={`p-2 xxs:p-2.5 xs:p-3 border rounded-lg ${
                              theme === 'light'
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-blue-900/20 border-blue-700/30'
                            }`}>
                              <p className={`text-[10px] xxs:text-xs font-medium mb-2 ${
                                theme === 'light' ? 'text-blue-700' : 'text-blue-400'
                              }`}>
                                참여 인원: {request.matched_members.length}명
                              </p>
                              <div className="flex gap-2 xxs:gap-2.5 xs:gap-3 overflow-x-auto pb-1">
                                {request.matched_members.map((member: any, index: number) => (
                                  <div
                                    key={member.id}
                                    className="flex flex-col items-center gap-1 flex-shrink-0"
                                  >
                                    <div className={`w-10 xxs:w-12 xs:w-14 h-10 xxs:h-12 xs:h-14 rounded-full overflow-hidden ${
                                      theme === 'light' ? 'bg-gray-200' : 'bg-gray-700'
                                    }`}>
                                      {member.profile_image ? (
                                        <img
                                          src={member.profile_image}
                                          alt={`다이버 ${index + 1}`}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <User className={`w-5 xxs:w-6 xs:w-7 h-5 xxs:h-6 xs:h-7 ${
                                            theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                                          }`} />
                                        </div>
                                      )}
                                    </div>
                                    <span className={`text-[9px] xxs:text-[10px] xs:text-xs font-medium ${
                                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                                    }`}>
                                      {member.name?.substring(0, 1)}**
                                    </span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[8px] xxs:text-[9px] font-medium ${getLevelColor(member.certification_level || '')}`}>
                                      {getLevelDisplay(member.certification_level || '')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* 버튼 그룹 */}
                          <div className="grid grid-cols-2 gap-1.5 xxs:gap-2">
                            {/* 매칭 상세 보기 버튼 */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                loadMatchDetails(request.id)
                              }}
                              className={`px-2 xxs:px-3 xs:px-4 py-1.5 xxs:py-2 rounded-lg transition-colors flex items-center justify-center gap-1 xxs:gap-1.5 xs:gap-2 text-xs xxs:text-sm whitespace-nowrap ${
                                theme === 'light'
                                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                  : 'bg-blue-700 hover:bg-blue-800 text-white'
                              }`}
                            >
                              <Eye className="w-3 xxs:w-3.5 xs:w-4 h-3 xxs:h-3.5 xs:h-4" />
                              상세 보기
                            </button>

                            {/* 프로필 보기 버튼 */}
                            {request.matched_members && request.matched_members.length > 0 && (
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    // 멤버가 1명이면 바로 모달, 여러명이면 선택 드롭다운 표시
                                    if (request.matched_members!.length === 1) {
                                      setSelectedProfileId(request.matched_members![0].id)
                                      setIsProfileModalOpen(true)
                                    } else {
                                      setShowMemberSelect(showMemberSelect === request.id ? null : request.id)
                                    }
                                  }}
                                  className={`px-2 xxs:px-3 xs:px-4 py-1.5 xxs:py-2 border rounded-lg transition-colors flex items-center justify-center gap-1 xxs:gap-1.5 xs:gap-2 text-xs xxs:text-sm whitespace-nowrap ${
                                    theme === 'light'
                                      ? 'bg-white border-blue-500 text-blue-600 hover:bg-blue-50'
                                      : 'bg-gray-800 border-blue-500 text-blue-400 hover:bg-gray-700'
                                  }`}
                                >
                                  <User className="w-3 xxs:w-3.5 xs:w-4 h-3 xxs:h-3.5 xs:h-4" />
                                  프로필 보기
                                  {request.matched_members!.length > 1 && (
                                    <ChevronRight className={`w-3 h-3 transition-transform ${showMemberSelect === request.id ? 'rotate-90' : ''}`} />
                                  )}
                                </button>

                                {/* 멤버 선택 드롭다운 */}
                                {showMemberSelect === request.id && request.matched_members!.length > 1 && (
                                  <div className={`absolute bottom-full left-0 mb-2 w-full min-w-[160px] rounded-lg shadow-lg z-10 overflow-hidden ${
                                    theme === 'light' ? 'bg-white border border-gray-200' : 'bg-gray-800 border border-gray-700'
                                  }`}>
                                    <div className={`px-3 py-2 text-xs font-medium border-b ${
                                      theme === 'light' ? 'bg-gray-50 text-gray-600 border-gray-200' : 'bg-gray-700 text-gray-400 border-gray-600'
                                    }`}>
                                      멤버 선택
                                    </div>
                                    {request.matched_members!.map((member: any) => (
                                      <button
                                        key={member.id}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setSelectedProfileId(member.id)
                                          setIsProfileModalOpen(true)
                                          setShowMemberSelect(null)
                                        }}
                                        className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-colors ${
                                          theme === 'light'
                                            ? 'hover:bg-blue-50 text-gray-700'
                                            : 'hover:bg-gray-700 text-gray-200'
                                        }`}
                                      >
                                        <div className={`w-6 h-6 rounded-full overflow-hidden flex-shrink-0 ${
                                          theme === 'light' ? 'bg-gray-200' : 'bg-gray-600'
                                        }`}>
                                          {member.profile_image ? (
                                            <img src={member.profile_image} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                              <User className="w-3 h-3 text-gray-400" />
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium truncate">{member.name}</p>
                                          <p className={`text-[10px] ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {getLevelDisplay(member.certification_level || '')}
                                          </p>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 매칭 확정 버튼 (숨김 처리) */}
                      {false && request.status === 'matched' && (
                        <button
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('auth_token')

                              // 1. 매칭 확정 API 호출
                              const confirmResponse = await fetch('/api/matching/confirm', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  match_request_id: request.id
                                })
                              })

                              const confirmResult = await confirmResponse.json()

                              if (!confirmResult.success) {
                                logger.error('Confirmation failed:', confirmResult)

                                // 이미 확정한 경우
                                if (confirmResult.error === 'Already confirmed') {
                                  // toast.info('이미 확정됨', '이미 매칭을 확정하셨습니다. 다른 사용자들을 기다리고 있습니다.')
                                  return
                                }

                                toast.error('매칭 확정 실패', confirmResult.error || 'Unknown error')
                                return
                              }

                              // 2. 확정 결과에 따라 처리
                              if (!confirmResult.data.in_top_4) {
                                toast.error('인원 초과', '4명이 먼저 확정하여 매칭이 취소되었습니다.')
                                loadMatchingRequests() // 목록 새로고침
                                return
                              }

                              if (confirmResult.data.is_complete) {
                                const roomId = confirmResult.data.chat_room_id
                                router.push(roomId ? `/chat?room=${roomId}` : '/chat')
                              } else {
                                // 아직 4명이 다 확정하지 않음
                                // toast.success(
                                //   '매칭 확정 완료',
                                //   `${confirmResult.data.confirmed_count}/4명이 확정했습니다.`
                                // )
                                loadMatchingRequests() // 목록 새로고침
                              }
                            } catch (error) {
                              logger.error('Match confirmation error:', error)
                              toast.error('오류 발생', '매칭 확정 중 오류가 발생했습니다.')
                            }
                          }}
                          className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Users className="w-4 h-4" />
                          매칭 확정하기
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                  </div>

                  {/* 하단 인원 정보 바 */}
                  {request.status === 'matched' || request.status === 'confirmed' ? (
                    <div className={`px-2 xxs:px-3 xs:p-4 py-2 xxs:py-2.5 border-t flex items-center justify-between ${
                      theme === 'light'
                        ? 'bg-blue-50 border-blue-100'
                        : 'bg-blue-900/10 border-blue-800/30'
                    }`}>
                      <div className="flex items-center gap-1.5 xxs:gap-2">
                        <Users className={`w-3.5 xxs:w-4 h-3.5 xxs:h-4 ${
                          theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                        }`} />
                        <span className={`text-xs xxs:text-sm font-medium ${
                          theme === 'light' ? 'text-blue-700' : 'text-blue-300'
                        }`}>
                          참여 인원: {request.member_count || 2}명
                        </span>
                      </div>
                      {request.status === 'confirmed' && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 bg-green-500 text-white rounded-full">
                            확정
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              openReviewModal(request)
                            }}
                            className="text-xs px-2 py-0.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
                          >
                            평가하기
                          </button>
                        </div>
                      )}
                    </div>
                  ) : request.status === 'expired' ? (
                    <div className={`border-t ${
                      theme === 'light'
                        ? 'bg-gray-100 border-gray-200'
                        : 'bg-gray-800/50 border-gray-700/50'
                    }`}>
                      {/* 매칭 종료된 버디 정보 표시 */}
                      {request.matched_members && request.matched_members.length > 0 && (
                        <div className={`px-2 xxs:px-3 xs:px-4 py-2 xxs:py-2.5 border-b ${
                          theme === 'light' ? 'border-gray-200' : 'border-gray-700/50'
                        }`}>
                          <p className={`text-[10px] xxs:text-xs font-medium mb-2 ${
                            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            함께 다이빙한 버디: {request.matched_members.length}명
                          </p>
                          <div className="flex gap-2 xxs:gap-2.5 xs:gap-3 overflow-x-auto pb-1">
                            {request.matched_members.map((member: any, index: number) => (
                              <div
                                key={member.id}
                                className="flex flex-col items-center gap-1 flex-shrink-0"
                              >
                                <div className={`w-10 xxs:w-12 xs:w-14 h-10 xxs:h-12 xs:h-14 rounded-full overflow-hidden ${
                                  theme === 'light' ? 'bg-gray-200' : 'bg-gray-700'
                                }`}>
                                  {member.profile_image ? (
                                    <img
                                      src={member.profile_image}
                                      alt={`다이버 ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <User className={`w-5 xxs:w-6 xs:w-7 h-5 xxs:h-6 xs:h-7 ${
                                        theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                                      }`} />
                                    </div>
                                  )}
                                </div>
                                <span className={`text-[9px] xxs:text-[10px] xs:text-xs font-medium ${
                                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                                }`}>
                                  {member.name?.substring(0, 1)}**
                                </span>
                                <span className={`px-1.5 py-0.5 rounded-full text-[8px] xxs:text-[9px] font-medium ${getLevelColor(member.certification_level || '')}`}>
                                  {getLevelDisplay(member.certification_level || '')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="px-2 xxs:px-3 xs:px-4 py-2 xxs:py-2.5 flex items-center gap-1.5 xxs:gap-2">
                        <Clock className={`w-3.5 xxs:w-4 h-3.5 xxs:h-4 ${
                          theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                        }`} />
                        <span className={`text-xs xxs:text-sm ${
                          theme === 'light' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          매칭 종료
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className={`px-2 xxs:px-3 xs:px-4 py-2 xxs:py-2.5 border-t ${
                      theme === 'light'
                        ? 'bg-gray-50 border-gray-100'
                        : 'bg-gray-800/50 border-gray-700/50'
                    }`}>
                      <div className="flex items-center gap-1.5 xxs:gap-2">
                        <Clock className={`w-3.5 xxs:w-4 h-3.5 xxs:h-4 ${
                          theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <span className={`text-xs xxs:text-sm ${
                          theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          매칭 대기중
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
      )}

      {/* 매칭 상세 모달 */}
      {showMatchModal && selectedMatchInfo && (
        <MatchingSuccessModal
          matchInfo={selectedMatchInfo}
          isOpen={showMatchModal}
          onClose={() => {
            setShowMatchModal(false)
            setSelectedMatchInfo(null)
          }}
          theme={theme}
        />
      )}

      {/* 프로필 모달 */}
      {selectedProfileId && (
        <ProfileModal
          userId={selectedProfileId}
          isOpen={isProfileModalOpen}
          onClose={() => {
            setIsProfileModalOpen(false)
            setSelectedProfileId(null)
          }}
        />
      )}

      {/* 버디 평가 모달 */}
      {reviewMatchInfo && (
        <BuddyReviewModalV2
          isOpen={isReviewModalOpen}
          onClose={() => {
            setIsReviewModalOpen(false)
            setReviewMatchInfo(null)
          }}
          matchInfo={reviewMatchInfo}
          onSubmit={handleReviewSubmit}
        />
      )}
    </div>
  )
}
